import './styles.css';
// @ts-expect-error 7016
import resize from '@alpinejs/resize';
import Alpine, { AlpineComponent } from 'alpinejs';
import PineconeRouter from 'pinecone-router';
import { Route, routes } from './routes.ts';
import { enableAnalytics } from './services/analytics.service.ts';
import { handleVersion, keepAlive } from './services/api.service.ts';
import { authenticationMiddleware } from './services/auth.service.ts';
import { Config, loadConfig } from './services/config.service.ts';
import { t } from './services/locale.service.ts';
import { initTheme } from './services/theme.service.ts';

declare global {
    interface Window {
        Alpine: typeof Alpine;
        apiHost: string;
        cache: Map<string, any>;
    }
}

export type ComponentModule<T = any> = {
    name: () => string;
    styles?: () => Promise<any>;
    template: () => Promise<{ default: string }>;
    data: () => AlpineComponent<
        T & {
            [key: string]: any;
            [key: number]: any;
            [key: symbol]: any;
            $t?: (key: string, count?: number) => string;
        }
    >;
};

export const render = async (module: any, route: string | undefined = undefined) => {
    const componentModule: ComponentModule = typeof module['default'] !== 'undefined' ? module.default : module;
    const name = componentModule.name === undefined ? '' : componentModule.name();

    Alpine.data(name, componentModule.data);

    if (componentModule.styles) {
        await componentModule.styles();
    }

    if (componentModule.template === undefined) {
        console.warn('Template not found!', componentModule);
        return '';
    }

    const componentTemplate = await componentModule.template();
    const html = `<div x-data="${name}">${componentTemplate.default}</div>`;

    return route ? `<template x-template x-route="${route}">${html}</template>` : html;
};

async function main() {
    let html: string = '';

    await Promise.all(
        routes.map(async (route: Route) => {
            const { path, component } = route;
            const content = await render(await component(), path);

            html += content;
        }),
    );

    // Toast component
    const toast = await import('@/components/shared/toast/toast.ts');
    html += await render(toast);

    if (window.cache === undefined) {
        window.cache = new Map<string, any>();
    }

    return Promise.resolve(html);
}

const defineApiHost = (config: Config): void => {
    console.info('MODE', import.meta.env?.MODE);

    // Development specific settings
    if (import.meta.env?.MODE === 'development') {
        console.log('Development environment');
        window.apiHost = '';
    }

    // Production specific settings
    if (import.meta.env?.MODE !== 'development') {
        window.apiHost = config.api.host;

        // php -S 0.0.0.0:5173 -t .\dist\
        // window.apiHost = 'https://dev-api.isiparts.com:8443';

        enableAnalytics();
    }
};

const defaultComponentSelector = (selector = '#app') => {
    const app = document.querySelector<HTMLDivElement>(selector);

    if (!app) {
        console.error(`${selector} not found!`);
        throw new Error(`${selector} not found!`);
    }

    return app;
};

export const defaultComponent = (): AlpineComponent<{ loading: boolean }> => {
    return Alpine.$data(defaultComponentSelector()) as AlpineComponent<{ loading: boolean }>;
};

main().then((html) => {
    const config = loadConfig();
    defineApiHost(config);

    const app = defaultComponentSelector();

    app.innerHTML = html;

    Alpine.plugin(PineconeRouter);
    Alpine.plugin(resize);
    // Add more Alpine plugins here
    Alpine.magic('t', () => (key: string, count: number) => {
        return t(key, count);
    });

    window.Alpine = Alpine;

    Alpine.start();

    initTheme(config.theme);
    keepAlive();
    handleVersion();
    authenticationMiddleware();
});
